// What the analyzing card says. It follows the steps the server reports (unpack, measure, write), so it
// only says what's really happening; a server that doesn't report them gets the plain listeningLine.
// Coach voice, sentence case, no exclamation marks (see the design system's content rules).

import { FULL_MIX } from './instruments';

// Most analyses finish well inside this; past it the card says it's taking longer
export const SLOW_SECONDS = 75;

const megabytes = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

/** While the file uploads: how much of it has gone */
export const uploadLine = (filename, sent, total) => `Sending ${filename} · ${megabytes(sent)} of ${megabytes(total)} MB`;

/** Once it's sent: what happens to it now, or that it's taking a while */
export const listeningLine = (category, seconds) => {
  if (seconds >= SLOW_SECONDS) return 'Taking longer than usual, but it’s still going.';
  return category === FULL_MIX
    ? 'Measuring its loudness, tone and stereo, then deciding how ready it is for mastering…'
    : 'Measuring its level, tone and stereo, then picking the one thing to fix first…';
};

// The checks the server ticks off while measuring, in the order the card shows them
export const CHECKS = [
  { id: 'level', label: 'Level' },
  { id: 'tone', label: 'Tone' },
  { id: 'stereo', label: 'Stereo' },
  { id: 'groove', label: 'Groove & key' },
];

const measureLine = (focus) => (focus
  ? `Measuring its level, tone, stereo and groove, with an ear on the ${focus} you mentioned…`
  : 'Measuring its level, tone, stereo and groove…');

/**
 * The card's heading, status line and ring caption for a server step. `what` is the clip in a sentence
 * ("hi-hat", "full mix"); `seconds` since the upload finished; `focus` the band their note talks about
 * ("low mids"), from the server.
 */
export const stepWords = (step, { what, filename, category, seconds, focus }) => {
  const slow = seconds >= SLOW_SECONDS;
  if (step === 'unpack') {
    return { title: 'Got it. Opening it up', line: slow ? listeningLine(category, seconds) : `Reading ${filename}…`, ring: 'opening' };
  }
  if (step === 'write') {
    const line = category === FULL_MIX ? 'Deciding how ready it is for mastering…' : 'Picking the one thing to fix first…';
    return { title: 'Writing up what I heard', line: slow ? listeningLine(category, seconds) : line, ring: 'writing' };
  }
  if (step === 'measure') {
    return { title: `Listening to your ${what}`, line: slow ? listeningLine(category, seconds) : measureLine(focus), ring: 'listening' };
  }
  return { title: `Listening to your ${what}`, line: listeningLine(category, seconds), ring: 'listening' };
};

/**
 * Where the stand-in spectrum leans when the browser can't draw the clip's waveform, so its bars
 * look like the instrument: 0 is all low end, 1 all top end.
 */
export const spectrumLean = (category, instrumentType) => {
  if (['Kick', '808', 'Sub bass'].includes(instrumentType) || category === 'Bass') return 0.15;
  if (['Hi-hat', 'Cymbals'].includes(instrumentType)) return 0.9;
  if (category === 'Vocals' || category === 'Guitar') return 0.55;
  return 0.4;
};
